import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@scio3/ui';
import { analyticsOptibotMessage } from '@/lib/analytics-service';
import { SIDEBAR_MAX_VW, useOptibotStore } from '../stores/optibot-store';
import { useOptiBot, setActiveImageIds } from '../hooks/useOptiBot';
import { usePageContext } from '../hooks/usePageContext';
import { useImageUpload } from '../hooks/useImageUpload';
import { useResizableSidebar } from '../hooks/useResizableSidebar';
import { OptiBotHeader } from './OptiBotHeader';
import { OptiBotMessages } from './OptiBotMessages';
import { OptiBotInput } from './OptiBotInput';
import { FocusedSessionPane } from './FocusedSessionPane';
import { OptiBotSessionHistory } from './OptiBotSessionHistory';
import { SupportCallout } from './SupportCallout';

/** Below this window width the docked sidebar would cramp the page, so it auto-closes. */
const MIN_SIDEBAR_WINDOW_PX = 1024;

/**
 * Right-side panel for OptiBot chat. Supports two view modes (optibot-store):
 * - sidebar:  docked right, drag-to-resize (pushes main content left)
 * - floating: detached card anchored bottom-right, overlays content
 *
 * Floating positions absolutely within the AppShell content row (which is
 * `relative`), so it sits below the header/banner without measuring heights.
 * Sidebar stays in the flex flow so it can push the page; AppShell only drops
 * the page's right padding in sidebar mode.
 */
export function OptiBotPanel() {
  const { t } = useTranslation();
  const isOpen = useOptibotStore((s) => s.isOpen);
  const viewMode = useOptibotStore((s) => s.viewMode);
  // Device-troubleshoot session state (ours): when a session is focused we show it
  // full-size in place of the chat; showHistory shows the session-history list.
  const focusedSession = useOptibotStore((s) => s.focusedSession);
  const showHistory = useOptibotStore((s) => s.showHistory);
  const {
    messages,
    sendMessage,
    isLoading,
    stop,
    input,
    setInput,
    handleApprove,
    handleDeny,
    handleFeedback,
    clearChat,
    loadConversation,
    getMessageTimestamp,
    isLoadingHistory,
    activeThreadId,
    retry,
  } = useOptiBot();
  const { suggestions } = usePageContext();
  const imageUpload = useImageUpload();
  const resize = useResizableSidebar();
  // Drives the header's scroll shadow — only on once the message list is scrolled
  // down (so nothing casts a shadow when pinned at the top).
  const [isScrolled, setIsScrolled] = useState(false);
  // Tooltip is anchored to where the cursor first enters the drag band — captured
  // once on mouse-enter and NOT updated on move, so it stays put (doesn't follow).
  const [tipPos, setTipPos] = useState<{ x: number; y: number } | null>(null);

  // Track whether the window is too narrow to dock the sidebar. When narrow, the
  // panel renders as a floating overlay instead of pushing/cramping the page (the
  // user's chosen viewMode is preserved and restored when the window grows back).
  const isNarrow = useOptibotStore((s) => s.isNarrow);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MIN_SIDEBAR_WINDOW_PX - 1}px)`);
    const update = () => useOptibotStore.getState().setIsNarrow(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // The Get Started checklist publishes its bottom-right visibility here. While it
  // shows, the docked sidebar would collide with the fixed checklist, so we force
  // floating (like narrow) and shrink the card so the checklist can stack above it.
  const checklistOpen = useOptibotStore((s) => s.checklistOpen);

  // Effective mode: force floating while narrow or while the checklist is up,
  // otherwise the user's choice.
  const effectiveViewMode = isNarrow || checklistOpen ? 'floating' : viewMode;

  const handleSend = useCallback(
    async (text: string, suggestionId?: string) => {
      // Set pending image IDs for the transport body (read by the body callback)
      const imageIds = imageUpload.getUploadedImageIds();
      if (imageIds.length > 0) {
        setActiveImageIds(imageIds);
      }

      // Collect FileUIParts for Claude vision (reads files as data URLs)
      const fileParts = imageUpload.hasImages ? await imageUpload.getFileUIParts() : undefined;

      analyticsOptibotMessage({ messageLength: text.length, suggestionId });

      sendMessage(
        {
          text,
          ...(fileParts?.length ? { files: fileParts } : {}),
        },
        suggestionId ? { body: { source: 'suggestion', suggestionId } } : undefined,
      );

      // Clear image previews after send
      imageUpload.clearAllImages();
    },
    [sendMessage, imageUpload],
  );

  // Read and clear the prompt from the store, not this render's value, so a StrictMode re-run sends nothing.
  const hasPendingPrompt = useOptibotStore((s) => s.pendingPrompt !== null);
  useEffect(() => {
    if (!isOpen || !hasPendingPrompt || isLoading) return;
    const { pendingPrompt, clearPendingPrompt } = useOptibotStore.getState();
    if (!pendingPrompt) return;
    clearPendingPrompt();
    clearChat();
    void handleSend(pendingPrompt, 'screen-menu');
  }, [isOpen, hasPendingPrompt, isLoading, clearChat, handleSend]);

  // Shared chat chrome (header + messages + input) inside the rounded card.
  // A single TooltipProvider covers all icon-button tooltips in the panel.
  const body = (
    <TooltipProvider delayDuration={300}>
      <div
        data-testid="optibot-panel-body"
        className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl bg-card"
      >
        <OptiBotHeader
          onNewChat={clearChat}
          onLoadConversation={loadConversation}
          activeThreadId={activeThreadId}
          hasMessages={messages.length > 0}
          isScrolled={isScrolled}
        />
        {/* Contact-support callout — pinned below the header, persists across chat, troubleshoot
            session, and history views (not only the chat). */}
        <SupportCallout />
        {focusedSession ? (
          // A session is focused → show it full-size in place of the chat (thread is preserved).
          <FocusedSessionPane />
        ) : showHistory ? (
          // Troubleshoot-session history list; a row opens into the FocusedSessionPane (history mode).
          <OptiBotSessionHistory />
        ) : (
          <>
            <OptiBotMessages
              messages={messages}
              isLoading={isLoading}
              isLoadingHistory={isLoadingHistory}
              onApprove={handleApprove}
              onDeny={handleDeny}
              onFeedback={handleFeedback}
              suggestions={suggestions}
              onSuggestionClick={handleSend}
              getMessageTimestamp={getMessageTimestamp}
              onScrolledChange={setIsScrolled}
              onRetry={retry}
            />
            <OptiBotInput
              input={input}
              setInput={setInput}
              onSend={handleSend}
              isLoading={isLoading}
              onStop={stop}
              images={imageUpload.images}
              isUploading={imageUpload.isUploading}
              fileInputRef={imageUpload.fileInputRef}
              acceptedExtensions={imageUpload.acceptedExtensions}
              onOpenFilePicker={imageUpload.openFilePicker}
              onFileSelect={imageUpload.handleFileSelect}
              onRemoveImage={imageUpload.removeImage}
            />
          </>
        )}
      </div>
    </TooltipProvider>
  );

  // --- Sidebar: in-flow, drag-to-resize, pushes content. Always rendered so
  //     the open/close width transition animates. ---
  if (effectiveViewMode === 'sidebar') {
    return (
      <aside
        role="complementary"
        aria-label="OptiBot"
        // Mounted-but-collapsed: keep it out of the a11y tree and tab order so
        // the hidden 0-width chat isn't focusable while minimized.
        aria-hidden={!isOpen}
        inert={!isOpen}
        className={cn(
          'relative flex shrink-0 bg-muted',
          isOpen && 'pb-2 pr-2',
          // Don't transition while actively dragging — it would lag the pointer.
          !resize.isResizing && 'transition-[width] duration-300 ease-in-out',
        )}
        style={{
          width: isOpen ? resize.width : 0,
          // No minWidth here — a CSS min-width can't transition, so it would snap
          // the panel open instead of sliding. Width is JS-clamped to the min in
          // useResizableSidebar; maxWidth is a viewport safety cap only.
          ...(isOpen ? { maxWidth: `${SIDEBAR_MAX_VW * 100}vw` } : {}),
        }}
      >
        {/* Body stays mounted even when closed so the width transition can slide
            it out on minimize (the body's own overflow-hidden clips it to 0). */}
        {body}
        {isOpen && (
          /* Resize handle — an invisible drag band sitting in the gutter to the
             left of the seam (the page keeps its px-3 md:px-6 padding in AppShell).
             No visible line; the col-resize cursor and tooltip are the affordance.
             The tooltip anchors to a fixed point at the cursor's entry position. */
          <TooltipProvider>
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label={t('optibot.resizeHandle')}
              onPointerDown={(e) => {
                setTipPos(null);
                resize.onPointerDown(e);
              }}
              onMouseEnter={(e) => setTipPos({ x: e.clientX, y: e.clientY })}
              onMouseLeave={() => setTipPos(null)}
              className="absolute inset-y-0 -left-1 z-10 w-4 -translate-x-1/2 cursor-col-resize md:-left-1.5"
            />
            <Tooltip open={tipPos !== null} onOpenChange={(o) => !o && setTipPos(null)}>
              <TooltipTrigger asChild>
                {/* Zero-size anchor pinned at the captured cursor position. */}
                <span
                  aria-hidden
                  className="pointer-events-none fixed block h-0 w-0"
                  style={{ left: tipPos?.x ?? 0, top: tipPos?.y ?? 0 }}
                />
              </TooltipTrigger>
              <TooltipContent side="bottom" align="start" sideOffset={8}>
                {t('optibot.resizeHandle')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </aside>
    );
  }

  if (!isOpen) return null;

  // --- Floating: detached card anchored to the bottom-right corner. ---
  // When the Get Started checklist is stacked above, the card uses the shorter
  // height so both fit; the checklist's raised offset is derived from the same
  // FLOATING_HEIGHT_WITH_CHECKLIST_PX constant (see GetStartedChecklist).
  return (
    <div
      role="complementary"
      aria-label="OptiBot"
      className={cn(
        'absolute bottom-4 right-4 z-40 flex w-[400px] max-w-[calc(100%-2rem)]',
        // Heights are kept in sync with the exported FLOATING_HEIGHT* constants:
        // 520px default, 420px while the checklist stacks above.
        checklistOpen ? 'h-[min(420px,calc(100%-2rem))]' : 'h-[min(520px,calc(100%-2rem))]',
        // Level-2 elevation: a floating window is the one app surface analogous
        // to a Radix floating panel, so shadow-md (not shadow-sm) is intentional.
        'rounded-xl border border-border shadow-md',
      )}
    >
      {body}
    </div>
  );
}
